import { produce } from "../utils/immer"
import { Edge } from "@/types/Edge"
import { create } from "zustand"
import { devtools, persist } from "zustand/middleware"

type EdgesState = {
  edges: Edge[]
  startConnectionID: string
  lastSaved: number | null
  saveStatus: 'idle' | 'saving' | 'saved' | 'error'
  integrationId: string | null // Track which integration these edges belong to
}

type EdgesAction = {
  setEdges: (edges: Edge[], integrationId?: string) => void
  deleteEdge: (id: string) => void
  setStartConnectionID: (id: string) => void
  setSaveStatus: (status: 'idle' | 'saving' | 'saved' | 'error') => void
  setLastSaved: (timestamp: number) => void
  clearEdges: () => void // New action to clear all edges
  setIntegrationId: (id: string | null) => void // New action to set integration ID
}

export const useEdgesStore = create<EdgesState & EdgesAction>()(
  devtools(
    persist(
      setStore => ({
        edges: [],
        lastSaved: null,
        saveStatus: 'idle',
        integrationId: null,
        setEdges: (edges, integrationId?) => {
          setStore(
            produce(state => {
              state.edges = edges
              if (integrationId !== undefined) {
                state.integrationId = integrationId
              }
            })
          )
        },
        deleteEdge: (id: string) => {
          setStore(
            produce(state => {
              state.edges = state.edges.filter((edge: Edge) => edge.id !== id)
            })
          )
        },
        startConnectionID: "",
        setStartConnectionID: id => {
          setStore(
            produce(state => {
              state.startConnectionID = id
            })
          )
        },
        setSaveStatus: (status) => {
          setStore(
            produce(state => {
              state.saveStatus = status
            })
          )
        },
        setLastSaved: (timestamp) => {
          setStore(
            produce(state => {
              state.lastSaved = timestamp
            })
          )
        },
        clearEdges: () => {
          setStore(
            produce(state => {
              state.edges = []
              state.integrationId = null
              state.lastSaved = null
              state.saveStatus = 'idle'
            })
          )
        },
        setIntegrationId: (id) => {
          setStore(
            produce(state => {
              state.integrationId = id
            })
          )
        },
      }),
      {
        name: "edges-storage", // unique name for localStorage key
        partialize: (state) => ({ 
          edges: state.edges,
          lastSaved: state.lastSaved,
          integrationId: state.integrationId 
        }), // Persist edges, lastSaved, and integrationId
      }
    ),
    { name: "edges" }
  )
)
